import React, { useState } from 'react';

/**
 * PauseMenu — in-match menu for resuming, restarting and leaving.
 *
 * Until this existed there was no way out of a match at all: `R` and `M` are
 * only listened for once a winner is decided, so a player who wanted to stop
 * mid-fight had to reload the page.
 *
 * Two things vary by mode, and both are about not lying to the player:
 *
 *  - **Local and vs-AI pause behind this menu; online does not.** The
 *    simulation hold gates the host's stepping and the guest's input sending,
 *    so an online player sitting on a paused menu would freeze their opponent.
 *    Online play carries on underneath, and the menu says so.
 *  - **Restart is host-only.** The online guest never simulates — it re-syncs
 *    to the host's snapshots — so a guest-side restart would be overwritten
 *    within a frame. Rather than offer a button that silently does nothing, the
 *    guest is told the host controls the rematch.
 *
 * Leaving takes two clicks. Opening a menu is deliberate but a misclick inside
 * one is not, and quitting is unrecoverable — doubly so online, where it drops
 * the opponent's match too.
 *
 * Both exits forfeit a staked series, because a stake you can walk away from
 * is not a stake — so when there is money on the match, both say so before
 * taking it. The rule is worth stating on screen rather than only in the code:
 * a player who discovers it by losing 100 will reasonably think it a bug.
 *
 * @param {object} props
 * @param {'local'|'ai'|'online'} props.mode
 * @param {boolean} props.canRestart  False for the online guest.
 * @param {number} [props.stakeAtRisk] Play money riding on an undecided
 *   series; 0 or absent when there is nothing to lose by leaving.
 * @param {() => void} props.onResume
 * @param {() => void} props.onRestart
 * @param {() => void} props.onQuit   Leaves the match (and closes any socket).
 */
const PauseMenu = ({
  mode, canRestart, stakeAtRisk = 0, onResume, onRestart, onQuit,
}) => {
  const [confirmingQuit, setConfirmingQuit] = useState(false);
  const online = mode === 'online';

  /** Buttons steal focus, and Space/Enter are player 2's controls. */
  const run = (fn) => (e) => {
    e.currentTarget.blur();
    fn();
  };

  return (
    <div className="controls-overlay">
      <div className="controls-panel pause-panel">
        {/* Not "Paused" online — the match is still running underneath, and the
            panel should not claim otherwise in its own heading. */}
        <h2>
          {confirmingQuit
            ? 'Leave the match?'
            : online ? 'Match Menu' : 'Paused'}
        </h2>

        {confirmingQuit ? (
          <>
            <p className="pause-warning">
              {online
                ? 'This ends the match for your opponent too — they will be dropped back to the menu.'
                : 'The current match will be lost. Your win record is kept.'}
            </p>
            <div className="pause-actions">
              <button className="start-btn pause-danger" onClick={run(onQuit)}>
                Yes, leave
              </button>
              <button className="lobby-back" onClick={run(() => setConfirmingQuit(false))}>
                ← Keep playing
              </button>
            </div>
          </>
        ) : (
          <>
            {online && (
              <p className="controls-live">
                The fight carries on while this is open — online matches cannot pause.
              </p>
            )}
            {stakeAtRisk > 0 && (
              <p className="pause-stake">
                {stakeAtRisk} staked on this series — restarting or quitting
                forfeits it.
              </p>
            )}
            <div className="pause-actions">
              <button className="start-btn" onClick={run(onResume)}>
                Resume · Esc
              </button>
              {canRestart ? (
                <button className="start-btn pause-secondary" onClick={run(onRestart)}>
                  {stakeAtRisk > 0 ? 'Restart — forfeit stake' : 'Restart match'}
                </button>
              ) : (
                <p className="pause-note">The host controls restarts.</p>
              )}
              <button className="lobby-back pause-quit" onClick={run(() => setConfirmingQuit(true))}>
                Quit to menu
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PauseMenu;
